
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { User, Video, LogOut, Volume2, Store } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { AvatarImage, AvatarFallback, Avatar } from '@/components/ui/avatar';

const Header = () => {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<any>(null);
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<string | null>(null);
  
  // Add scroll listener to toggle header background
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  // Check if user is logged in, get location and profile data
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session) {
          setUser(session.user);
          fetchUserProfile(session.user.id);
          
          // Set up realtime subscription to listen for profile updates
          const channel = supabase
            .channel('profile-points-updates')
            .on('postgres_changes', 
              { 
                event: 'UPDATE', 
                schema: 'public', 
                table: 'profile',
                filter: `user_id=eq.${session.user.id}`
              }, 
              (payload) => {
                if (payload.new) {
                  setProfile(prev => ({ ...prev, ...payload.new }));
                }
              }
            )
            .subscribe();
            
          return () => {
            supabase.removeChannel(channel);
          };
        }
      } catch (error) {
        console.error("Auth error:", error);
      }
    };
    
    // Get current location
    const getLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          position => {
            const { latitude, longitude } = position.coords;

            fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`)
              .then(response => response.json())
              .then(data => {
                if (data && data.address) {
                  const { road, suburb, city, state, postcode, country } = data.address;

                  const locationParts = [
                    road || suburb || city || '',  // fallback for best immediate location
                    state,
                    postcode,
                    country
                  ].filter(Boolean); // removes undefined/null

                  setCurrentLocation(locationParts.join(', '));
                } else {
                  setCurrentLocation(`${latitude.toFixed(2)}, ${longitude.toFixed(2)}`);
                }
              })
              .catch(error => {
                console.error("Error getting location name:", error);
                setCurrentLocation(`${latitude.toFixed(2)}, ${longitude.toFixed(2)}`);
              });
          },
          error => {
            console.error("Error getting location:", error);
            setCurrentLocation("Location unavailable");
          }
        );
      } else {
        setCurrentLocation("Geolocation not supported");
      }
    };
    
    const fetchUserProfile = async (userId: string) => {
      try {
        const { data, error } = await supabase
          .from('profile')
          .select('*')
          .eq('user_id', userId)
          .single();

        if (error) {
          console.error('Error fetching profile:', error);
        } else {
          setProfile(data);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };
    
    checkAuth();
    getLocation();
  }, []);

  const handleLogout = async () => {
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      toast.error("Failed to log out");
    } else {
      setUser(null);
      setProfile(null);
      toast.success("Logged out successfully");
      navigate('/');
    }
  };

  // Function to get points value, handling null and string/number parsing
  const getUserPoints = () => {
    if (!profile) return '0';
    
    const points = profile.total_points;
    if (points === null || points === undefined) return '0';
    
    return String(points);
  };

  
  return (
    // Make header fully transparent
<header className={`fixed w-full top-0 z-40 bg-transparent flex flex-col ${scrolled ? 'scrolled' : ''}`}>
  <div className="w-full px-2 py-1">
    <div className="flex items-center">
       <div className="flex items-center">
            <Link to="/" className="text-white font-bold text-lg flex items-center">
              <img 
                src="/lovable-uploads/GrinGauge_Logo.png" 
                alt="GrinGauge" 
                className="h-20 w-auto mr-1" 
              />              
            </Link>
            
          </div>         
          <div className="flex items-center space-x-4">
                 <Link to="/" className="text-white font-bold text-lg flex items-center">
              <img 
                src="/lovable-uploads/VideoWall.png" 
                alt="Wall" 
                className="h-5 w-auto mr-1" 
              />              
            </Link>
            <Link to="/record" className="text-white font-bold text-lg flex items-center">
              <img 
                src="/lovable-uploads/Recorder.png" 
                alt="Record" 
                className="h-5 w-auto mr-1" 
              />              
            </Link>
            
            <Link to="/merchants" className="text-white font-bold text-lg flex items-center">
              <Store className="h-5 w-5 mr-1" />
              <span className="hidden sm:inline">Merchants</span>
            </Link>
            
            {user ? (
              <div className="flex items-center space-x-2">
                                
                <Avatar onClick={() => navigate('/profile')} className="cursor-pointer">
                  <AvatarImage 
                    src={user.user_metadata?.avatar_url || user.user_metadata?.picture} 
                    alt={user.email || "User"}
                  />
                  <AvatarFallback>
                    {user.email?.charAt(0).toUpperCase() || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col items-start mr-1">
  <span className="text-sm font-small text-white">
    {user.user_metadata?.name || user.email?.split('@')[0] || "User"}
  </span>
  <span className="text-xs text-gray-300">
    {getUserPoints()} points
  </span>
</div>
                <Button variant="ghost" size="sm" onClick={handleLogout}>
                  <LogOut className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate('/login')}>
                <User className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">Login</span>
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Location display at bottom of header */}
      <div className="header-location w-full text-center text-[6px]" style={{ marginTop: '-22px' }}>
        {currentLocation ? (
          <span>@ {currentLocation}</span>
        ) : (
          <span>Loading location...</span>
        )}
      </div>
    </header>
  );
};

export default Header;
